import { useState, useRef, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Loader2, UploadCloud, FileText, Check } from 'lucide-react'
import { toast } from 'sonner'
import {
  useUploadDraft,
  useQualityControlMembers,
  useLayoutAndDesignMembers,
  useEditorialManagers,
  usePostProductionMembers,
} from '../../api/hooks'
import { useWireAuth } from '../../auth/useWireAuth'
import { useTopics, useRegions } from '../../hooks/useTaxonomy'
import { TaxonomyPicker } from '../common/TaxonomyPicker'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'
import type { GroupMember } from '../../api/types'

// ─── Member row ───────────────────────────────────────────────────────────────

function MemberRow({ member, isSelected, isMe, onToggle }: {
  member: GroupMember
  isSelected: boolean
  isMe: boolean
  onToggle: () => void
}) {
  const initials = (member.name
    ? member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)
    : member.username.slice(0, 2)
  ).toUpperCase()
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-fill"
    >
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
        style={isSelected
          ? { backgroundColor: 'var(--blue-4)', color: 'var(--blue-11)' }
          : { backgroundColor: 'var(--gray-3)', color: 'var(--gray-11)' }}
        aria-hidden="true"
      >
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-semibold text-fg">
          {member.name || member.username}
          {isMe && <span className="ml-1 text-[10px] font-normal text-fg-secondary">(you)</span>}
        </p>
        {member.agency && <p className="truncate text-[10px] text-fg-secondary">{member.agency}</p>}
      </div>
      {isSelected && <Check size={11} style={{ color: 'var(--blue-11)' }} aria-hidden="true" />}
    </button>
  )
}

// ─── Role column ──────────────────────────────────────────────────────────────

function RoleColumn({ label, accentClass, members, isPending, isError, selectedSub, currentUserSub, onToggle }: {
  label: string
  accentClass: string
  members: GroupMember[]
  isPending: boolean
  isError: boolean
  selectedSub: string | null
  currentUserSub: string
  onToggle: (sub: string) => void
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5 border-b border-line px-3 py-2">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${accentClass}`} aria-hidden="true" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-fg">{label}</span>
        <span className="ml-auto shrink-0 text-[10px]" style={{ color: selectedSub ? 'var(--blue-11)' : 'var(--amber-11)' }}>
          {selectedSub ? (selectedSub === currentUserSub ? 'You' : `···${selectedSub.slice(-5)}`) : 'Unassigned'}
        </span>
      </div>
      <div className="max-h-36 overflow-y-auto">
        {isPending && <div className="flex justify-center py-4"><Loader2 size={12} className="animate-spin text-fg-secondary" /></div>}
        {isError && <p className="px-3 py-3 text-[10px] text-error-fg">Failed to load members.</p>}
        {!isPending && !isError && members.length === 0 && <p className="px-3 py-3 text-[10px] text-fg-secondary">No members.</p>}
        {members.map((m) => (
          <MemberRow
            key={m.username}
            member={m}
            isSelected={m.username === selectedSub}
            isMe={m.username === currentUserSub}
            onToggle={() => onToggle(m.username)}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

type Props = { open: boolean; onOpenChange: (open: boolean) => void }

export function NewArticleDialog({ open, onOpenChange }: Props) {
  const { user } = useWireAuth()
  const currentUserSub = user?.username ?? user?.sub ?? ''
  const { mutateAsync: uploadDraft, isPending } = useUploadDraft()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data: topicOptions = [], isLoading: topicsLoading } = useTopics()
  const { data: regionOptions = [], isLoading: regionsLoading } = useRegions()

  const qcQ        = useQualityControlMembers()
  const layoutQ    = useLayoutAndDesignMembers()
  const editorialQ = useEditorialManagers()
  const postPubQ   = usePostProductionMembers()

  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [cmtResult, setCmtResult] = useState<CmtResult | undefined>(undefined)
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [sensitive, setSensitive] = useState(false)
  const [qcSub,        setQc]        = useState<string | null>(null)
  const [layoutSub,    setLayout]    = useState<string | null>(null)
  const [editorialSub, setEditorial] = useState<string | null>(null)
  const [postPubSub,   setPostPub]   = useState<string | null>(null)

  const canSubmit = file !== null && cmtResult !== undefined && !isPending

  function acceptFile(f: File) {
    if (!f.name.endsWith('.docx')) {
      toast.error('Only .docx files are supported')
      return
    }
    setFile(f)
  }

  function reset() {
    setFile(null)
    setIsDragging(false)
    setCmtResult(undefined)
    setSelectedTopics([])
    setSelectedRegions([])
    setSensitive(false)
    setQc(user?.sub ?? null)
    setLayout(null)
    setEditorial(null)
    setPostPub(null)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) acceptFile(dropped)
  }, [])

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0]
    if (picked) acceptFile(picked)
    e.target.value = ''
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit || !file) return
    try {
      const article = await uploadDraft({
        file,
        metadata: {
          classification: cmtResult.classification,
          disseminationControls: cmtResult.disseminationControls.length > 0 ? cmtResult.disseminationControls : undefined,
          qcAssignedTo:        qcSub        ?? undefined,
          layoutAssignedTo:    layoutSub    ?? undefined,
          editorialAssignedTo: editorialSub ?? undefined,
          postPubAssignedTo:   postPubSub   ?? undefined,
          topics:  selectedTopics.length  > 0 ? selectedTopics  : undefined,
          regions: selectedRegions.length > 0 ? selectedRegions : undefined,
          sensitive: sensitive || undefined,
        },
      })
      toast.success('Article created', { description: article.documentId ?? article.id })
      handleOpenChange(false)
    } catch (err) {
      toast.error('Failed to create article', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  function toggle(setter: React.Dispatch<React.SetStateAction<string | null>>, sub: string) {
    setter((prev) => (prev === sub ? null : sub))
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[620px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-surface shadow-xl focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <Dialog.Title className="text-base font-semibold text-fg">New Article</Dialog.Title>
            <Dialog.Close className="rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg">
              <X size={14} aria-hidden="true" />
            </Dialog.Close>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)}>
            {/* Scrollable body */}
            <div className="max-h-[calc(100dvh-10rem)] overflow-y-auto">

              {/* Metadata section */}
              <div className="space-y-4 p-5">
                {/* Drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !file && fileInputRef.current?.click()}
                  className={`flex min-h-28 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-colors ${
                    isDragging
                      ? 'border-accent-primary-solid bg-accent-primary-subtle'
                      : file
                        ? 'cursor-default border-line bg-canvas'
                        : 'border-line bg-canvas hover:border-line-strong'
                  }`}
                >
                  {file ? (
                    <>
                      <FileText size={24} className="text-accent-primary-solid" aria-hidden="true" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-fg">{file.name}</p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setFile(null) }}
                          className="mt-1 text-xs text-fg-secondary underline hover:text-fg"
                        >
                          Remove
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <UploadCloud size={24} className="text-fg-secondary" aria-hidden="true" />
                      <div className="text-center">
                        <p className="text-sm text-fg-secondary">
                          Drop a <span className="font-semibold text-fg">.docx</span> file here
                        </p>
                        <button
                          type="button"
                          className="mt-1 text-xs text-accent-primary-solid underline hover:opacity-80"
                          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click() }}
                        >
                          or browse to upload
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept=".docx" className="sr-only" onChange={handleFileInput} />

                {/* Classification */}
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Classification <span className="text-error-fg">*</span>
                  </label>
                  <CmtWrapper
                    value={cmtResult}
                    onChange={setCmtResult}
                  />
                </div>

                {/* Topics + Regions side by side */}
                <div className="grid grid-cols-2 gap-4">
                  <TaxonomyPicker
                    label="Topics"
                    options={topicOptions}
                    selected={selectedTopics}
                    onChange={setSelectedTopics}
                    placeholder="No topics selected"
                    loading={topicsLoading}
                  />
                  <TaxonomyPicker
                    label="Regions"
                    options={regionOptions}
                    selected={selectedRegions}
                    onChange={setSelectedRegions}
                    placeholder="No regions selected"
                    loading={regionsLoading}
                  />
                </div>

                {/* Sensitive */}
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={sensitive}
                    onChange={(e) => setSensitive(e.target.checked)}
                    className="h-3.5 w-3.5 rounded accent-accent-primary-solid"
                  />
                  <span className="text-xs font-semibold text-fg">Sensitive</span>
                  <span className="text-[11px] text-fg-secondary">— requires elevated access to view</span>
                </label>
              </div>

              {/* Role assignment grid */}
              <div className="border-t border-line">
                <p className="px-5 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Assign Roles
                </p>
                <div className="grid grid-cols-2 divide-x divide-y divide-line border-t border-line">
                  <RoleColumn
                    label="QC Review"
                    accentClass="bg-yellow-400"
                    members={qcQ.data ?? []}
                    isPending={qcQ.isPending}
                    isError={qcQ.isError}
                    selectedSub={qcSub}
                    currentUserSub={currentUserSub}
                    onToggle={(sub) => toggle(setQc, sub)}
                  />
                  <RoleColumn
                    label="Layout & Design"
                    accentClass="bg-blue-400"
                    members={layoutQ.data ?? []}
                    isPending={layoutQ.isPending}
                    isError={layoutQ.isError}
                    selectedSub={layoutSub}
                    currentUserSub={currentUserSub}
                    onToggle={(sub) => toggle(setLayout, sub)}
                  />
                  <RoleColumn
                    label="Editorial Approval"
                    accentClass="bg-violet-400"
                    members={editorialQ.data ?? []}
                    isPending={editorialQ.isPending}
                    isError={editorialQ.isError}
                    selectedSub={editorialSub}
                    currentUserSub={currentUserSub}
                    onToggle={(sub) => toggle(setEditorial, sub)}
                  />
                  <RoleColumn
                    label="Post-Pub Review"
                    accentClass="bg-orange-400"
                    members={postPubQ.data ?? []}
                    isPending={postPubQ.isPending}
                    isError={postPubQ.isError}
                    selectedSub={postPubSub}
                    currentUserSub={currentUserSub}
                    onToggle={(sub) => toggle(setPostPub, sub)}
                  />
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              <Dialog.Close
                className="rounded px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                disabled={isPending}
              >
                Cancel
              </Dialog.Close>
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex items-center gap-1.5 rounded bg-accent-primary-solid px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-50"
              >
                {isPending && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
                Upload
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
